import * as actionTypes from './actionTypes'
import { getTargetPGAForm } from '../api/getEntrySummaryData'
import { convertFDAFOOItems } from '../formSections/util/assignValues'
import { initializeTargetPGA, initializeTargetPGAWithTemplates } from './initializeValues'

// Constants
const CONSTANTS = {
  FAKE_GUID_PREFIX: 'FAKEGUID_',
  MIN_PGA_ITEMS: 3,
  NHT_REI_FORM_TYPE: 'NHT_REI',
  PGA_KEYS: {
    INDIVIDUAL_NAME_CN: 'individualNameCN',
    TEL_NUMBER_CN: 'telNumberCN',
    EMAIL_CN: 'emailCN'
  }
};

// Helper function to enhance NHT_REI response with missing consignee fields
function enhanceNHTREIResponse(response) {
  const eventMessage = response?.data?.eventMessage;
  const pgaItems = eventMessage?.pgaItems;
  
  if (!pgaItems?.length) {
    return response;
  }
  
  const existingKeys = new Set(pgaItems.map(itm => itm?.key));
  
  const missingFields = [
    {
      key: CONSTANTS.PGA_KEYS.INDIVIDUAL_NAME_CN,
      exists: existingKeys.has(CONSTANTS.PGA_KEYS.INDIVIDUAL_NAME_CN),
      data: {
        key: CONSTANTS.PGA_KEYS.INDIVIDUAL_NAME_CN,
        dataType: "STRING",
        isOptional: "Y",
        uiLabel: "Individual Name",
        uiInputType: "DISPLAY",
        uiLabelDesc: "Name of Individual",
        PGAId: CONSTANTS.PGA_KEYS.INDIVIDUAL_NAME_CN,
        isRepeated: "N",
        lengthClass: "32X"
      }
    },
    {
      key: CONSTANTS.PGA_KEYS.TEL_NUMBER_CN,
      exists: existingKeys.has(CONSTANTS.PGA_KEYS.TEL_NUMBER_CN),
      data: {
        key: CONSTANTS.PGA_KEYS.TEL_NUMBER_CN,
        dataType: "STRING",
        isOptional: "Y",
        uiLabel: "Telephone Number",
        uiInputType: "DISPLAY",
        uiLabelDesc: "Contact Telephone Number",
        PGAId: CONSTANTS.PGA_KEYS.TEL_NUMBER_CN,
        isRepeated: "N",
        lengthClass: "15X"
      }
    },
    {
      key: CONSTANTS.PGA_KEYS.EMAIL_CN,
      exists: existingKeys.has(CONSTANTS.PGA_KEYS.EMAIL_CN),
      data: {
        key: CONSTANTS.PGA_KEYS.EMAIL_CN,
        dataType: "STRING",
        isOptional: "Y",
        uiLabel: "Email",
        uiInputType: "DISPLAY",
        uiLabelDesc: "Contact Email Address",
        PGAId: CONSTANTS.PGA_KEYS.EMAIL_CN,
        isRepeated: "N",
        lengthClass: "50X"
      }
    }
  ];
  
  missingFields
    .filter(field => !field.exists)
    .forEach(field => pgaItems.push(field.data));
  
  return response;
}

export function updatePGAField(pgaGuid, targetPGA, merchandiseId, field, payload) {
  return {
    type: actionTypes.UPDATE_PGA_FIELD({ pgaGuid, targetPGA }),
    payload,
    field,
    pgaGuid,
  }
}

export function savePGAField(pgaGuid, targetPGA, merchandiseId, lineItemIndex, field) {
  return {
    type: actionTypes.SAVE_PGA_FIELD({ pgaGuid, targetPGA }),
    field,
    merchandiseId,
    pgaGuid,
  }
}

export function copyPgaItems(pgaGuid, targetPGA, lineItemIndex, merchandiseId, pgaItems) {
  return {
    type: actionTypes.COPY_PGA_FIELD({merchandiseId, pgaGuid, targetPGA}),
    lineItemIndex,
    merchandiseId,
    payload : convertFDAFOOItems(pgaItems,merchandiseId,pgaGuid, targetPGA),
    pgaItems,
    pgaGuid
  }
}

// Helper function to create repeated sub-field actions with container cardinality check
function createRepeatedSubAction({
  containerCardinality,
  fallbackAction,
  actionType,
  pgaGuid,
  targetPGA,
  merchandiseId,
  baseFields,
  additionalFields = {}
}) {
  if (containerCardinality < 0) {
    return fallbackAction;
  }
  
  return {
    type: actionType({ merchandiseId, targetPGA, pgaGuid }),
    ...baseFields,
    ...additionalFields,
    containerCardinality,
    pgaGuid
  };
}

export function updateRepeatedSubField(pgaGuid, targetPGA, merchandiseId, index, field, payload, list, containerCardinality) {
  return createRepeatedSubAction({
    containerCardinality,
    fallbackAction: updateRepeatedField(pgaGuid, targetPGA, merchandiseId, index, field, payload, list),
    actionType: actionTypes.UPDATE_REPEATED_SUB_FIELD,
    pgaGuid,
    targetPGA,
    merchandiseId,
    baseFields: { index, field, payload, list }
  });
}
export function updateRepeatedField(pgaGuid, targetPGA, merchandiseId, index, field, payload, list) {
  return {
    type: actionTypes.UPDATE_REPEATED_FIELD({ merchandiseId, targetPGA, pgaGuid }),
    index,
    field,
    payload,
    list,
    pgaGuid
  }
}

export function saveRepeatedSubField(pgaGuid, targetPGA, merchandiseId, lineItemIndex, index, field, list, containerCardinality) {
  return createRepeatedSubAction({
    containerCardinality,
    fallbackAction: saveRepeatedField(pgaGuid, targetPGA, merchandiseId, lineItemIndex, index, field, list),
    actionType: actionTypes.SAVE_REPEATED_SUB_FIELD,
    pgaGuid,
    targetPGA,
    merchandiseId,
    baseFields: { index, field, list },
    additionalFields: { merchandiseId }
  });
}
export function saveRepeatedField(pgaGuid, targetPGA, merchandiseId, lineItemIndex, index, field, list) {
  return {
    type: actionTypes.SAVE_REPEATED_FIELD({ merchandiseId, targetPGA, pgaGuid }),
    index,
    field,
    merchandiseId,
    list,
    pgaGuid
  }
}

export function addRepeatedSubItem(pgaGuid, targetPGA, merchandiseId, lineItemIndex, list, containerCardinality, initialValues_th) {
  return createRepeatedSubAction({
    containerCardinality,
    fallbackAction: addRepeatedItem(pgaGuid, targetPGA, merchandiseId, lineItemIndex, list),
    actionType: actionTypes.ADD_REPEATED_SUB_ITEM,
    pgaGuid,
    targetPGA,
    merchandiseId,
    baseFields: { list },
    additionalFields: { merchandiseId, initialValues_th }
  });
}
export function replaceAllRepeatedItem(pgaGuid, targetPGA, merchandiseId, lineItemIndex, list, newItemsToReplace) {
  return {
    type: actionTypes.REPEATED_ITEM_REPLACE_ALL({ merchandiseId, targetPGA, pgaGuid }),
    merchandiseId,
    list,
    newItemsToReplace,
    pgaGuid,
  }
}

export function addRepeatedItemArray(pgaGuid, targetPGA, merchandiseId, lineItemIndex, list, arrayOfNewItemsToAdd) {
  return {
    type: actionTypes.ADD_REPEATED_ITEM_ARRAY({ merchandiseId, targetPGA, pgaGuid }),
    merchandiseId,
    list,
    arrayOfNewItemsToAdd,
    pgaGuid,
  }

}

export function addRepeatedItem(pgaGuid, targetPGA, merchandiseId, lineItemIndex, list, newItemsToAdd) {
  return {
    type: actionTypes.ADD_REPEATED_ITEM({ merchandiseId, targetPGA, pgaGuid }),
    merchandiseId,
    list,
    newItemsToAdd,
    pgaGuid,
  }
}

export function removeRepeatedSubGroup(pgaGuid, targetPGA, merchandiseId, lineItemIndex, index, list, containerCardinality) {
  return createRepeatedSubAction({
    containerCardinality,
    fallbackAction: removeRepeatedGroup(pgaGuid, targetPGA, merchandiseId, lineItemIndex, index, list),
    actionType: actionTypes.REMOVE_REPEATED_SUB_GROUP,
    pgaGuid,
    targetPGA,
    merchandiseId,
    baseFields: { list },
    additionalFields: { payload: index, merchandiseId }
  });
}
export function removeRepeatedSubSubGroup(pgaGuid,targetPGA, merchandiseId,  index, list,containerCardinality,innerContainerCardinality) {
  return {
    type: actionTypes.REMOVE_REPEATED_SUB_SUB_GROUP({ merchandiseId, targetPGA, pgaGuid }),
    payload: index,
    merchandiseId,
    list,
    containerCardinality,
    innerContainerCardinality,
    pgaGuid,
  }
}
export function addRepeatedSubSubItem(pgaGuid,targetPGA, merchandiseId, lineItemIndex, list,containerCardinality,innerContainerCardinality,initialValues_th) {
  return {
    type: actionTypes.ADD_REPEATED_SUB_SUB_ITEM({ merchandiseId, targetPGA, pgaGuid }),
    merchandiseId,
    list,
    containerCardinality,
    innerContainerCardinality,
    initialValues_th,
    pgaGuid,
  }
}
export function updateRepeatedSubSubField(pgaGuid,targetPGA, merchandiseId, index, field, payload, list,containerCardinality,innerContainerCardinality) {
  return {
    type: actionTypes.UPDATE_REPEATED_SUB_SUB_FIELD({ merchandiseId, targetPGA, pgaGuid }),
    index,
    field,
    payload,
    list,
    containerCardinality,
    innerContainerCardinality,
    pgaGuid,
  }
}
export function saveRepeatedSubSubField(pgaGuid,targetPGA, merchandiseId, lineItemIndex, index, field, list,containerCardinality,innerContainerCardinality) {
  return {
    type: actionTypes.SAVE_REPEATED_SUB_SUB_FIELD({ merchandiseId, targetPGA, pgaGuid }),
    index,
    field,
    merchandiseId,
    list,
    containerCardinality,
    innerContainerCardinality,
    pgaGuid,
  }
}
export function removeRepeatedGroup(pgaGuid,targetPGA, merchandiseId, lineItemIndex, index, list) {
  return {
    type: actionTypes.REMOVE_REPEATED_GROUP({ merchandiseId, targetPGA, pgaGuid }),
    payload: index,
    merchandiseId,
    list,
    pgaGuid,
  }
}

//good for doing a ton of things in a PGA at once
export function deleteAndReplaceMultiplePGAValues(pgaGuid,targetPGA, merchandiseId, lineItemIndex, index, list, newListOfItems,alsoAddThisSave) {
  return {
    type: actionTypes.REMOVE_REPEATED_GROUP({ merchandiseId, targetPGA, pgaGuid }),
    payload: index,
    merchandiseId,
    list,
    pgaGuid,
    newListOfItems,
    alsoAddThisSave,
  }
}
//same as above 
export function replaceRepeatedSubGroup(pgaGuid,targetPGA, merchandiseId, containerCardinality, index, listname, newListOfItems,alsoAddThisSave) {
  return {
    type: actionTypes.REPLACE_REPEATED_SUB_GROUP({ merchandiseId, targetPGA, pgaGuid }),
    payload: index,
    merchandiseId,
    listname,
    pgaGuid,
    newListOfItems,
    alsoAddThisSave,
    containerCardinality,
  }
}


export function addRepeatedSection(pgaGuid,targetPGA, merchandiseId, lineItemIndex, sectionConfig) {
  return {
    type: actionTypes.ADD_REPEATED_SECTION({ merchandiseId, targetPGA, pgaGuid }),
    merchandiseId,
    sectionConfig,
    pgaGuid,
  }
}

export function removeRepeatedSection(pgaGuid,targetPGA, merchandiseId, lineItemIndex, index, listArray) {
  return {
    type: actionTypes.REMOVE_REPEATED_SECTION({ merchandiseId, targetPGA, pgaGuid }),
    payload: index,
    merchandiseId,
    listArray,
    pgaGuid,
  }
}

export const togglePGA = (targetPGA, pgaGuid, merchandiseId, loading) => ({
  type: actionTypes.TOGGLE_PGA({ pgaGuid, targetPGA }),
  payload: merchandiseId,
  pgaGuid,
  loading
})

// CHANGED: Converted from direct store.dispatch() to Redux thunk pattern
export function togglePGAForm(pgaFlagInfos, merchandiseId = null, pgaGuid, targetPGA, lineItemIndex, 
  additionalPGAOptions, collapsing) {
  return (dispatch, getState) => {
    dispatch(togglePGA(targetPGA, pgaGuid, merchandiseId, true))
    
    if(collapsing){
      return dispatch(
        initializeTargetPGA(
          targetPGA,
          pgaGuid,
          {
            loading: true,
            pgaGuid,
            merchandiseId
          }
        )
      )
    }
    
    getTargetPGAForm(targetPGA, pgaGuid, merchandiseId, additionalPGAOptions)
      .then(response => {
        const eventMessage = response?.data?.eventMessage;
        const pgaItems = eventMessage?.pgaItems;
        const responsePgaGuid = eventMessage?.pgaGuid;
        
        if (!pgaItems || pgaItems.length < CONSTANTS.MIN_PGA_ITEMS) {
          return dispatch(
            initializeTargetPGA(
              targetPGA,
              responsePgaGuid,
              {
                error: `ERROR: ${targetPGA} information for the associated HTS and Merchandise ID ${merchandiseId} did come with all required PGA Items.`,
                merchandiseId
              }
            )
          )
        }
        const finalPgaGuid = responsePgaGuid || `${CONSTANTS.FAKE_GUID_PREFIX}${targetPGA}${merchandiseId}`;
        pgaGuid = finalPgaGuid;

        if (targetPGA === CONSTANTS.NHT_REI_FORM_TYPE) {
          enhanceNHTREIResponse(response);
        }
        return dispatch(
          initializeTargetPGAWithTemplates(
            targetPGA,
            finalPgaGuid,
            convertFDAFOOItems(
              pgaItems,
              merchandiseId,
              pgaGuid,
              targetPGA
            ),
            eventMessage?.templates
          )
        )
      })
      .catch(err => {
        if (process.env.NODE_ENV === 'development') {
          console.error(`${targetPGA} Error:`, err)
        }
        return dispatch(
          initializeTargetPGA(
            targetPGA,
            pgaGuid,
            {
              error: `ERROR: Could not retrieve ${targetPGA} information for the associated HTS and Merchandise ID ${merchandiseId}`,
              merchandiseId
            }
          )
        )
      })
  }
}

// CONTAINERS
export function updateContainerPGA(pgaGuid,targetPGA, merchandiseId, index, containerNum) {
  return {
    type: actionTypes.UPDATE_CONTAINER_PGA({ merchandiseId, targetPGA, pgaGuid }),
    payload: containerNum,
    index,
    pgaGuid,
  }
}

export function addContainerPGA(pgaGuid, targetPGA, merchandiseId) {
  return {
    type: actionTypes.ADD_CONTAINER_PGA({ pgaGuid, merchandiseId, targetPGA }),
    pgaGuid,
  }
}

export function removeContainerPGA(pgaGuid, targetPGA, merchandiseId, lineItemIndex, index) {
  return {
    type: actionTypes.REMOVE_CONTAINER_PGA({ merchandiseId, targetPGA, pgaGuid }),
    payload: index,
    merchandiseId,
    pgaGuid
  }
}

export function saveContainerPGA(pgaGuid, targetPGA, merchandiseId, lineItemIndex, index) {
  return {
    type: actionTypes.SAVE_CONTAINER_PGA({ merchandiseId, targetPGA, pgaGuid }),
    payload: index,
    merchandiseId,
    pgaGuid
  }
}
